import React, { useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { comprimirImagen } from "@/lib/image";
import { fechaHoraEn } from "@/lib/chofer";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import CamaraDocumento from "@/components/CamaraDocumento";
import { ScanPapelDialog } from "@/components/chofer/ScanPapelDialog";
import { Camera, FileUp, Loader2, FileText, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";
// Fix Q (2026-10-13): «Add PDF or photo». Cada archivo queda en su etapa, con el chofer y la hora real de subida
// (también se puede subir después de salir de la etapa). En el Signed DO / POD la foto se escanea con la revisión de IA.
export function AgregarArchivo({ cargaId, etapa, tipo, escaner = false, etiqueta = t("Add PDF or photo"), onCarga }) {
  const soloFoto = tipo === "container_photo";
  const titulo = soloFoto ? t("Add container photo") : etiqueta;
  const [abierto, setAbierto] = useState(false);
  const [camara, setCamara] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [scan, setScan] = useState(null); // {loading, result}
  const inputRef = useRef(null);
  const subir = async (file) => {
    if (soloFoto && !["image/jpeg", "image/png"].includes(file.type)) { toast.error(t("Upload a JPG or PNG container photo.")); return; }
    setSubiendo(true);
    try {
      const f = await comprimirImagen(file);
      const fd = new FormData();
      fd.append("etapa", etapa);
      fd.append("tipo", tipo);
      fd.append("file", f);
      const { data } = await api.post(`/chofer/cargas/${cargaId}/archivos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("File uploaded"));
      onCarga(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSubiendo(false); }
  };
  const alElegir = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    setAbierto(false);
    if (f) await subir(f);
  };
  const alCapturar = async (file) => {
    setCamara(false);
    if (!escaner) { await subir(file); return; }
    setScan({ loading: true, result: null });
    try {
      const comprimida = await comprimirImagen(file);
      const fd = new FormData();
      fd.append("file", comprimida);
      const { data } = await api.post(`/cargas/${cargaId}/escanear`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScan({ loading: false, result: data });
    } catch (err) {
      toast.error(apiError(err));
      setScan(null);
    }
  };
  return (
    <>
      <Button type="button" variant="outline" data-testid={`agregar-${etapa}`} disabled={subiendo} onClick={() => setAbierto(true)} className="w-full h-[52px] text-[16px] font-bold text-navy-2">
        {subiendo ? <Loader2 className="h-5 w-5 animate-spin" /> : titulo}
      </Button>
      <Sheet open={abierto} onOpenChange={setAbierto}>
        <SheetContent side="bottom" className="rounded-t-2xl" style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
          <SheetHeader><SheetTitle>{titulo}</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-2">
            <Button type="button" data-testid="agregar-foto" variant="outline" className="w-full h-12 justify-start" onClick={() => { setAbierto(false); setTimeout(() => setCamara(true), 200); }}>
              <Camera className="h-5 w-5" />{t(" Take a photo")}</Button>
            <Button type="button" data-testid="agregar-elegir" variant="outline" className="w-full h-12 justify-start" onClick={() => inputRef.current?.click()}>
              <FileUp className="h-5 w-5" />{soloFoto ? t("Choose a photo") : t(" Choose a PDF or photo")}</Button>
          </div>
        </SheetContent>
      </Sheet>
      <input ref={inputRef} data-testid="agregar-input" type="file" accept={soloFoto ? ".jpg,.jpeg,.png,image/jpeg,image/png" : ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"} className="hidden" onChange={alElegir} />
      {camara && <CamaraDocumento titulo={escaner ? t("Frame the signed paper") : t("Frame the document")} onCapture={alCapturar} onCancel={() => setCamara(false)} />}
      {scan && (
        <ScanPapelDialog
          scan={scan}
          cargaId={cargaId}
          onRetry={() => { setScan(null); setTimeout(() => setCamara(true), 150); }}
          onClose={() => setScan(null)}
          onSaved={(data) => { setScan(null); onCarga(data); }}
        />
      )}
    </>
  );
}
// Abre un archivo de la carga en otra pestaña.
export async function abrirArchivo(fileId) {
  if (!fileId) return;
  const ventana = window.open("about:blank", "_blank");
  if (ventana) ventana.opener = null;
  try {
    const res = await api.get(`/documentos/${fileId}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    if (ventana && !ventana.closed) ventana.location.replace(url);
    else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (ventana && !ventana.closed) ventana.close();
    toast.error(apiError(e));
  }
}
// Archivos ya subidos de una etapa: nombre y hora de subida en la zona de la etapa.
export function ListaArchivos({ archivos, zona }) {
  if (!archivos.length) return null;
  return (
    <ul className="mt-2 space-y-1.5" data-testid="lista-archivos">
      {archivos.map((a) => (
        <li key={a.id}>
          <button type="button" data-testid={`archivo-${a.id}`} onClick={() => abrirArchivo(a.file_id)} className="w-full flex items-center justify-between gap-2 text-left rounded-lg border border-line bg-surface-2 px-3 py-2 min-h-[48px]">
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-[14.5px] font-semibold text-ok"><CheckCircle2 className="h-4 w-4 shrink-0" /><span className="truncate">{a.nombre}</span></span>
              <span className="block text-[12.5px] text-ink-2">{t("Uploaded {v0}", { v0: fechaHoraEn(a.subido_at, zona) })}{a.origen === "oficina" ? t(" · added by the office") : ""}</span>
            </span>
            {a.mime === "application/pdf" ? <FileText className="h-4 w-4 text-ink-3 shrink-0" /> : <Download className="h-4 w-4 text-ink-3 shrink-0" />}
          </button>
        </li>
      ))}
    </ul>
  );
}
